import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import React, { FC, useContext, useEffect, useState } from 'react';

import createFlowActivator from '../../api/flows/create-activator';
import { deleteFlowActivator } from '../../api/flows/delete-activator';
import editFlowActivator from '../../api/flows/edit-activator';
import { AppContext } from '../../AppContext';
import Input from '../../common/inputs/InputWithIcon';
import ThreeDotsMenu from '../../common/threeDotsMenu/three-dots-menu.component';
import { BotContext } from '../bot.context';
import ModalScriptInfo from '../script/modal-script.info';

import CreateEditActivator from './create-edit-activator';
import {
  CreateFlowActivator,
  IFlowActivator,
  IFlowWithActivatorsAndFolder,
  UpdateFlow,
} from './interfaces';

interface FlowActivatorsModalProps {
  flow: IFlowWithActivatorsAndFolder | null;
}

const FlowActivatorsModal: FC<FlowActivatorsModalProps> = ({ flow }) => {
  const { bot, refetchFlows } = useContext(BotContext);
  const { openConfirmationDialog } = useContext(AppContext);

  const [selectedActivator, setSelectedActivator] =
    useState<IFlowActivator | null>(null);
  const [filteredActivators, setFilteredActivators] = useState<
    IFlowActivator[] | null
  >(null);
  const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
  const [creatingNewActivator, setCreatingNewActivator] = useState(false);
  const [scriptModalOpen, setScriptModalOpen] = useState<boolean>(false);
  const [isCopyActivator, setIsCopyActivator] = useState<boolean>(false);
  const [filterInput, setFilterInput] = useState<string>('');

  const onCreateNewActivator = (newFlowActivator: CreateFlowActivator) => {
    if (!flow) return;

    createFlowActivator(flow?._id, {
      ...newFlowActivator,
    }).then(() => {
      refetchFlows();
      setCreatingNewActivator(false);
    });
  };

  const onEditActivator = (activatorToUpdate: UpdateFlow) => {
    if (!selectedActivator || !flow) return;

    editFlowActivator(
      flow?._id,
      selectedActivator?._id,
      activatorToUpdate,
    ).then(refetchFlows);
  };

  const onCopyActivator = () => {
    setIsCopyActivator(true);
  };

  const onResetSelectedActivator = () => {
    setSelectedActivator(null);
  };

  const onOpenScriptModal = () => {
    setScriptModalOpen(true);
  };

  const onCloseScriptModal = () => {
    setScriptModalOpen(false);
  };

  const openProjectMenu = (
    e: React.SyntheticEvent,
    activator: IFlowActivator,
  ) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setSelectedActivator(activator);
  };

  useEffect(() => {
    setSelectedActivator(
      flow?.activators.find(
        (activator) => activator._id === selectedActivator?._id,
      ) ?? null,
    );
  }, [flow]);

  useEffect(() => {
    setIsCopyActivator(false);
  }, [selectedActivator, creatingNewActivator]);

  useEffect(() => {
    if (!flow) return;
    setFilteredActivators(
      flow?.activators?.filter((activator) =>
        activator?.name.toLowerCase().includes(filterInput.toLowerCase()),
      ),
    );
  }, [flow, filterInput]);

  return (
    <Stack
      direction="row"
      gap="10px"
      sx={{
        minHeight: '500px',
      }}
    >
      <Stack
        sx={{
          width: '240px',
          alignSelf: 'start',
          height: '100%',
        }}
      >
        <Stack
          sx={{
            position: 'relative',
          }}
        >
          <Input
            value={filterInput}
            handleChangeSearch={(e) => setFilterInput(e.target.value)}
            withIcon={true}
          />
        </Stack>
        {Boolean(filteredActivators?.length) ? (
          <Stack
            sx={{
              display: flow?.activators.length ? 'flex' : 'none',

              overflow: 'hidden',
              maxHeight: '400px',
              height: 'auto',
              overflowY: 'auto',
              mt: '10px',
            }}
          >
            {filteredActivators?.map((activator) => (
              <Stack
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  mt: '8px',
                  transition: 'all .1s',
                  padding: '5px 15px',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  height: '47px',
                  backgroundColor:
                    activator._id === selectedActivator?._id && !isCopyActivator
                      ? 'grey.13'
                      : 'grey.15',
                }}
                key={activator._id}
                onClick={() => {
                  setSelectedActivator(activator);
                  setCreatingNewActivator(false);
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '18px',
                    color:
                      activator._id === selectedActivator?._id &&
                      !isCopyActivator
                        ? 'green.2'
                        : 'grey.1',
                  }}
                >
                  {activator.name}
                </Typography>
                <Stack onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    onClick={(e) => openProjectMenu(e, activator)}
                    sx={{
                      alignItems: 'start',
                      height: 'fit-content',
                      padding: '0',
                      color: 'grey.5',
                    }}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                  <ThreeDotsMenu
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    anchor={menuAnchor}
                    border="1px solid"
                    borderColor="grey.12"
                    borderRadius="8px"
                    backgroundColor="grey.15"
                    hoverBackground="grey.15"
                    menuItems={[
                      [
                        {
                          text: t('common.copy'),
                          renderIcon: () => (
                            <ContentCopyIcon
                              sx={{
                                color: 'grey.1',
                                fontSize: '16px',
                                opacity: '0.8',
                              }}
                            />
                          ),
                          onClick: () => {
                            onCopyActivator();
                          },
                        },
                      ],
                      [
                        {
                          text: t('common.delete'),
                          renderIcon: () => (
                            <DeleteIcon
                              sx={{
                                color: 'red.2',
                                fontSize: '16px',
                                opacity: '0.8',
                              }}
                            />
                          ),
                          onClick: () => {
                            openConfirmationDialog(
                              `${t('activators.deleteActivatorLink')} ${
                                selectedActivator?.name
                              }?`,
                              t('common.delete'),
                              () => {
                                deleteFlowActivator(
                                  flow?._id ?? '',
                                  selectedActivator?._id ?? '',
                                ).then(refetchFlows);
                              },
                            );
                          },
                        },
                      ],
                    ]}
                  />
                </Stack>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Stack sx={{ mt: '20px' }}>
            <Typography
              sx={{
                textAlign: 'center',
                fontWeight: 500,
                fontSize: '14px',
                opacity: '0.6',
              }}
            >
              {t('common.activators.notFound')}
            </Typography>
          </Stack>
        )}
        <Stack sx={{ mt: 'auto' }}>
          <Button
            onClick={onOpenScriptModal}
            startIcon={
              <svg
                width="18"
                height="13"
                viewBox="0 0 18 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.96959 7.52992L3.96959 10.5299C4.03877 10.6016 4.12153 10.6587 4.21304 10.698C4.30454 10.7373 4.40295 10.758 4.50254 10.7589C4.60212 10.7597 4.70088 10.7407 4.79306 10.703C4.88523 10.6653 4.96897 10.6096 5.03939 10.5392C5.10981 10.4688 5.1655 10.3851 5.20321 10.2929C5.24092 10.2007 5.25989 10.102 5.25903 10.0024C5.25816 9.90279 5.23747 9.80437 5.19817 9.71287C5.15886 9.62137 5.10172 9.53861 5.03009 9.46942L2.56034 6.99967L5.03009 4.52992C5.10172 4.46074 5.15886 4.37798 5.19817 4.28648C5.23747 4.19497 5.25816 4.09656 5.25903 3.99697C5.25989 3.89739 5.24092 3.79863 5.20321 3.70646C5.1655 3.61428 5.10981 3.53055 5.03939 3.46013C4.96897 3.38971 4.88523 3.33402 4.79306 3.29631C4.70088 3.2586 4.60212 3.23962 4.50254 3.24048C4.40295 3.24135 4.30454 3.26204 4.21304 3.30135C4.12153 3.34065 4.03877 3.39779 3.96959 3.46942L0.96959 6.46942C0.828987 6.61007 0.75 6.8008 0.75 6.99967C0.75 7.19855 0.828987 7.38928 0.96959 7.52992ZM14.0301 3.46942C13.9609 3.39779 13.8781 3.34065 13.7866 3.30135C13.6951 3.26204 13.5967 3.24135 13.4971 3.24048C13.3976 3.23962 13.2988 3.2586 13.2066 3.29631C13.1145 3.33402 13.0307 3.38971 12.9603 3.46013C12.8899 3.53055 12.8342 3.61428 12.7965 3.70646C12.7588 3.79863 12.7398 3.89739 12.7407 3.99697C12.7415 4.09656 12.7622 4.19497 12.8015 4.28648C12.8408 4.37798 12.898 4.46074 12.9696 4.52992L15.4393 6.99967L12.9696 9.46942C12.898 9.53861 12.8408 9.62137 12.8015 9.71287C12.7622 9.80437 12.7415 9.90279 12.7407 10.0024C12.7398 10.102 12.7588 10.2007 12.7965 10.2929C12.8342 10.3851 12.8899 10.4688 12.9603 10.5392C13.0307 10.6096 13.1145 10.6653 13.2066 10.703C13.2988 10.7407 13.3976 10.7597 13.4971 10.7589C13.5967 10.758 13.6951 10.7373 13.7866 10.698C13.8781 10.6587 13.9609 10.6016 14.0301 10.5299L17.0301 7.52992C17.1707 7.38928 17.2497 7.19855 17.2497 6.99967C17.2497 6.8008 17.1707 6.61007 17.0301 6.46942L14.0301 3.46942ZM9.77909 1.54417L6.77909 12.0442C6.72448 12.2353 6.74799 12.4403 6.84447 12.6142C6.94094 12.788 7.10248 12.9164 7.29359 12.9712C7.36078 12.9897 7.43013 12.9993 7.49984 12.9997C7.66283 12.9996 7.82134 12.9463 7.95138 12.8481C8.08143 12.7499 8.17593 12.6119 8.22059 12.4552L11.2206 1.95517C11.2519 1.85929 11.2635 1.75808 11.2547 1.6576C11.246 1.55712 11.217 1.45944 11.1697 1.3704C11.1223 1.28136 11.0575 1.20279 10.979 1.13939C10.9006 1.07599 10.8102 1.02905 10.7132 1.0014C10.6162 0.973746 10.5146 0.965938 10.4145 0.978444C10.3145 0.99095 10.2179 1.02351 10.1307 1.07418C10.0435 1.12485 9.96745 1.19259 9.90703 1.27335C9.8466 1.3541 9.80309 1.44621 9.77909 1.54417Z"
                  fill="#CAFA44"
                />
              </svg>
            }
            sx={{
              border: 'none',
              color: 'green.2',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            {t('common.activators.modalScript')}
          </Button>
          <Button
            className="green"
            sx={{
              fontWeight: 600,
              height: '56px',
              mt: '12px',
            }}
            onClick={() => {
              setCreatingNewActivator(true);
              setSelectedActivator(null);
            }}
          >
            {t('activators.newLink')}
          </Button>
        </Stack>
      </Stack>
      <Stack
        sx={{
          width: '1px',
          height: '100%',
          backgroundColor: 'grey.10',
        }}
      />
      <Stack
        sx={{
          width: '420px',
          alignSelf: 'start',
          height: '100%',
        }}
      >
        {!selectedActivator && !creatingNewActivator && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              width: '100%',
            }}
          >
            <Typography
              sx={{
                color: 'grey.5',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: '500',
                mt: '12px',
              }}
            >
              {t('channels.channelActivators.emptySelect')}
            </Typography>
          </Stack>
        )}
        {(creatingNewActivator || selectedActivator || isCopyActivator) && (
          <CreateEditActivator
            isCopyActivator={isCopyActivator}
            onCreateNewActivator={onCreateNewActivator}
            onEditActivator={onEditActivator}
            creatingNewActivator={creatingNewActivator}
            selectedActivator={selectedActivator}
            onResetSelectedActivator={onResetSelectedActivator}
          />
        )}
      </Stack>
      <ModalScriptInfo
        scriptModalOpen={scriptModalOpen}
        handleScriptModalClose={onCloseScriptModal}
        type="flow"
        flowId={flow?._id ?? ''}
        botId={flow?.telegramBot ?? ''}
        botUsername={bot?.username ?? ''}
      />
    </Stack>
  );
};

export default FlowActivatorsModal;
